'use client';

import React, { useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { NewsFilters as NewsFiltersType, FilterOption } from '@/types/filters';
import styles from './NewsFilters.module.scss';

interface NewsFiltersProps {
  filters: NewsFiltersType;
  onFiltersChange: (filters: NewsFiltersType) => void;
  subCategories?: FilterOption[];
  availableTags?: FilterOption[];
  showSubCategoryFilter?: boolean;
  showDateFilter?: boolean;
  showTagFilter?: boolean;
}

const predefinedDateRanges: FilterOption[] = [
  { value: 'all', label: 'すべての期間' },
  { value: 'today', label: '今日' },
  { value: 'week', label: '過去1週間' },
  { value: 'month', label: '過去1ヶ月' },
  { value: 'quarter', label: '過去3ヶ月' },
  { value: 'year', label: '過去1年' },
  { value: 'custom', label: 'カスタム期間' }
];

export const NewsFilters: React.FC<NewsFiltersProps> = ({
  filters,
  onFiltersChange,
  subCategories = [],
  availableTags = [],
  showSubCategoryFilter = true,
  showDateFilter = true,
  showTagFilter = true,
}) => {
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [selectedDateRange, setSelectedDateRange] = React.useState('all');
  const [showCustomDateInputs, setShowCustomDateInputs] = React.useState(false);

  // Filter change handlers
  const handleSubCategoryChange = useCallback((value: string) => {
    onFiltersChange({
      ...filters,
      subCategory: value === 'all' ? undefined : value
    });
  }, [filters, onFiltersChange]);

  const handleDateRangeChange = useCallback((value: string) => {
    setSelectedDateRange(value);
    setShowCustomDateInputs(value === 'custom');
    
    if (value === 'all') {
      onFiltersChange({
        ...filters,
        startDate: undefined,
        endDate: undefined
      });
      return;
    }

    if (value === 'custom') {
      // Don't update filters until custom dates are entered
      return;
    }

    const now = new Date();
    let startDate: Date;
    
    switch (value) {
      case 'today':
        startDate = new Date(now.setHours(0, 0, 0, 0));
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
        break;
      case 'quarter':
        startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
        break;
      case 'year':
        startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
        break;
      default:
        return;
    }

    onFiltersChange({
      ...filters,
      startDate: startDate.toISOString().split('T')[0],
      endDate: new Date().toISOString().split('T')[0]
    });
  }, [filters, onFiltersChange]);

  const handleCustomDateChange = useCallback((type: 'start' | 'end', value: string) => {
    if (type === 'start') {
      onFiltersChange({
        ...filters,
        startDate: value || undefined
      });
    } else {
      onFiltersChange({
        ...filters,
        endDate: value || undefined
      });
    }
  }, [filters, onFiltersChange]);

  const handleTagToggle = useCallback((tag: string) => {
    const currentTags = filters.tags || [];
    const newTags = currentTags.includes(tag)
      ? currentTags.filter(t => t !== tag)
      : [...currentTags, tag];
    
    onFiltersChange({
      ...filters,
      tags: newTags.length > 0 ? newTags : undefined
    });
  }, [filters, onFiltersChange]);

  const handleClearFilters = useCallback(() => {
    onFiltersChange({});
    setSelectedDateRange('all');
    setShowCustomDateInputs(false);
  }, [onFiltersChange]);

  // Check if any filters are active
  const hasActiveFilters = useMemo(() => {
    return !!(filters.subCategory || filters.startDate || filters.endDate || (filters.tags && filters.tags.length > 0));
  }, [filters]);

  return (
    <div className={styles.newsFilters}>
      <div className={styles.filterHeader}>
        <button
          className={styles.filterToggle}
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label="フィルターを表示/非表示"
        >
          <svg 
            className={styles.filterIcon} 
            width="20" 
            height="20" 
            viewBox="0 0 20 20"
            aria-hidden="true"
          >
            <path 
              d="M3 5h14M5 10h10M7 15h6" 
              stroke="currentColor" 
              strokeWidth="2" 
              strokeLinecap="round"
            />
          </svg>
          <span>フィルター</span>
          {hasActiveFilters && (
            <span className={styles.activeIndicator} aria-label="フィルター適用中" />
          )}
        </button>
        
        {hasActiveFilters && (
          <button
            className={styles.clearButton}
            onClick={handleClearFilters}
            aria-label="すべてのフィルターをクリア"
          >
            クリア
          </button>
        )}
      </div>

      <AnimatePresence>
        {isExpanded && (
          <motion.div
            className={styles.filterContent}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <div className={styles.filterGrid}>
              {/* SubCategory Filter */}
              {showSubCategoryFilter && subCategories.length > 0 && (
                <div className={styles.filterGroup}>
                  <label htmlFor="subcategory-filter" className={styles.filterLabel}>
                    種類
                  </label>
                  <select
                    id="subcategory-filter"
                    className={styles.filterSelect}
                    value={filters.subCategory || 'all'}
                    onChange={(e) => handleSubCategoryChange(e.target.value)}
                  >
                    <option value="all">すべて</option>
                    {subCategories.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                        {option.count !== undefined && ` (${option.count})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Date Range Filter */}
              {showDateFilter && (
                <div className={styles.filterGroup}>
                  <label htmlFor="date-filter" className={styles.filterLabel}>
                    日付
                  </label>
                  <select
                    id="date-filter"
                    className={styles.filterSelect}
                    value={selectedDateRange}
                    onChange={(e) => handleDateRangeChange(e.target.value)}
                  >
                    {predefinedDateRanges.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Custom Date Inputs */}
              <AnimatePresence>
                {showCustomDateInputs && (
                  <motion.div
                    className={styles.customDateInputs}
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <div className={styles.dateInputGroup}>
                      <label htmlFor="start-date" className={styles.dateLabel}>
                        開始日
                      </label>
                      <input
                        id="start-date"
                        type="date"
                        className={styles.dateInput}
                        value={filters.startDate || ''}
                        onChange={(e) => handleCustomDateChange('start', e.target.value)}
                      />
                    </div>
                    <div className={styles.dateInputGroup}>
                      <label htmlFor="end-date" className={styles.dateLabel}>
                        終了日
                      </label>
                      <input
                        id="end-date"
                        type="date"
                        className={styles.dateInput}
                        value={filters.endDate || ''}
                        onChange={(e) => handleCustomDateChange('end', e.target.value)}
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Tag Filter */}
            {showTagFilter && availableTags.length > 0 && (
              <div className={styles.tagFilterGroup}>
                <div className={styles.filterLabel} role="group" aria-label="タグフィルター">タグ</div>
                <div className={styles.tagList}>
                  {availableTags.map((tag) => (
                    <button
                      key={tag.value}
                      className={`${styles.tagButton} ${
                        filters.tags?.includes(tag.value) ? styles.active : ''
                      }`}
                      onClick={() => handleTagToggle(tag.value)}
                      aria-pressed={filters.tags?.includes(tag.value)}
                    >
                      {tag.label}
                      {tag.count !== undefined && (
                        <span className={styles.tagCount}>({tag.count})</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};